import { useEffect, useState } from "react";

/**
 * #1201: how long something that is still going has been going, in whole seconds, from the
 * instant it started; null with no start (nothing running, or a start the server has not
 * reported). It ticks once a second only while it has a start, so a finished run costs no timer;
 * the first tick may read up to a second old.
 * A start in the future (a clock ahead of this one) reads 0, never a negative time.
 */

const TICK_MS = 1_000;

function elapsedSecondsSince(startedAt: string | null | undefined, now: number): number | null {
  if (!startedAt) return null;
  const started = Date.parse(startedAt);
  if (!Number.isFinite(started)) return null;
  return Math.max(0, Math.floor((now - started) / 1_000));
}

export function useElapsedSeconds(startedAt: string | null | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  const active = elapsedSecondsSince(startedAt, now) !== null;

  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [active]);

  return elapsedSecondsSince(startedAt, now);
}
